/**
 * Roster sync — self-roster publish + pull + auto-republish.
 *
 * A "roster" is a small DAG-JSON doc an agent publishes under a dedicated IPNS
 * name (a W3Name), advertising its own shares so other depots can discover and
 * subscribe to them. This module implements the full IPFS/IPNS round-trip by
 * reusing the existing `pushIOInWorker` / `publishIPNSRecord` plumbing (same
 * path as `pushShare`) — it does NOT build new IPNS infra.
 *
 * Publish: build a one-block DAG-JSON CAR client-side → `SyncWorker.pushIOInWorker`.
 * Pull:    one-shot IPNS resolve (`GET <ipnsWatcherUrl>/name/<ipns>`) → fetch the
 *          single block via `retrieveCarBlock` → `dagJson.decode`.
 * Discover: for each shareID in a pulled roster, auto-subscribe (unless it's our
 *          own share or already subscribed) and record the grouping on the source.
 */

import type { IpnsString } from '@wovin/core/applog'
import type { IRoster, IRosterSource } from '../data/agent/AgentRoster'
import { CarWriter } from '@ipld/car'
import * as dagJson from '@ipld/dag-json'
import { privateKeyToProtobuf } from '@libp2p/crypto/keys'
import { DEFAULT_GATEWAYS, retrieveCarBlock } from '@wovin/connect-gateway'
import { dateNowIso } from '@wovin/core/applog'
import { encodeBlock } from '@wovin/core/ipfs'
import { Logger } from 'besonders-logger'
import { CID } from 'multiformats/cid'
import * as W3Name from 'w3name'
import { useAgent } from '../data/agent/AgentState'
import { upsertSubscription } from './share-sync'
import { storageState } from './storage'
import { getSyncWorker } from './worker-utils'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG, { prefix: '[roster-sync]' })

/** Shape of a self-roster payload published to IPNS. */
export interface RosterPayload {
	depotID: string // agent.ag — the publishing depot's short hash
	depotName: string // agent.agentStringNoDID — human label without the DID
	rosterName?: string // user-set name of the roster on the publisher side
	agentString: string // full agentString (with DID) for display / keying
	shares: Record<string, string> // shareID -> description (override ?? share.name)
}

// =============================================================================
// Create / mint
// =============================================================================

/**
 * Mint a new W3Name for a self-roster and persist the resulting `IRoster`.
 *
 * Mirrors `addShare` (share-sync.ts:64-98): `W3Name.create()` gives a
 * `WritableName`; we store its private key as protobuf bytes (`pk`), the *exact
 * same shape as `IShare.pk`*, so the worker's `publishIPNSRecord` (which accepts
 * raw *or* protobuf bytes) takes it identically to a share's key. The roster's
 * `id` is the W3Name string (k51...), used as the IPNS name.
 */
export async function createSelfRoster(name?: string): Promise<IRoster> {
	const agent = useAgent()
	LOG('Creating self-roster', { name })

	const writable = await W3Name.create()
	// `key` is a libp2p PrivateKey; re-encode to protobuf so it round-trips through
	// `W3Name.from(pk)` (same as IShare.pk) and matches publishIPNSRecord's input.
	const pk = privateKeyToProtobuf(writable.key) as Uint8Array
	const id = writable.toString()
	DEBUG('minted W3Name for roster', { id })

	const roster: IRoster = {
		id,
		pk,
		name: name ?? `roster ${id.slice(-7)}`,
		createdAt: dateNowIso(),
		isDeleted: false,
		lastPush: null,
		lastPayloadHash: undefined,
	}
	await agent.addRoster(roster)
	return roster
}

/**
 * Build the DAG-JSON payload for a self-roster: the depot's identity fields plus
 * a map of every non-deleted share's id → description (descriptionOverrides win).
 */
export function buildSelfRosterPayload(roster: IRoster): RosterPayload {
	const agent = useAgent()
	// Opt-in membership: a share is published in this roster only if its id is
	// listed in `includedShareIDs`. (undefined/absent/empty → no shares; the
	// share-side multiselect in SharesSettings manages this allowlist.)
	const allowSet = new Set(roster.includedShareIDs ?? [])
	const shares: Record<string, string> = {}
	for (const share of agent.shares) {
		if (share.isDeleted) continue
		if (!share.id) continue
		if (!allowSet.has(share.id)) continue
		shares[share.id] = roster.descriptionOverrides?.[share.id] ?? share.name
	}
	return {
		depotID: agent.ag,
		depotName: agent.agentStringNoDID,
		rosterName: roster.name,
		agentString: agent.agentString,
		shares,
	}
}

// =============================================================================
// Publish
// =============================================================================

/** Concatenate an array of Uint8Array chunks into a single Uint8Array. */
function concatChunks(chunks: Uint8Array[]): Uint8Array {
	const total = chunks.reduce((n, c) => n + c.length, 0)
	const out = new Uint8Array(total)
	let offset = 0
	for (const c of chunks) {
		out.set(c, offset)
		offset += c.length
	}
	return out
}

/**
 * Encode `payload` as a single DAG-JSON block (via the same `encodeBlock` helper
 * applog blocks use → dag-json codec 0x0129), bundle it into a one-block CAR,
 * and push via `pushIOInWorker` — the same worker path `pushShare` uses (blocks
 * → IDB → car-mirror → kubo →
 * `publishIPNSRecord(pk, rootCID, targets)`). After success the roster's IPNS
 * name points at our block CID. Rosters are stateless single blocks, so
 * `prevCID` is left undefined (no snapshot chain / prevBoundary).
 *
 * On success, persists `lastPush` / `lastCID` / `lastPayloadHash` on the roster.
 */
export async function pushRosterDoc(roster: IRoster, payload: RosterPayload): Promise<CID> {
	const agent = useAgent()
	DEBUG('[pushRosterDoc]', { roster: roster.id, payload })

	const { bytes, cid } = encodeBlock(payload) // dag-json codec (0x0129) — matches every applog block; RAW (0x55) was wrong and the provider/car-mirror/IPNS path is built around dag-json roots
	DEBUG('[pushRosterDoc] block', { cid: cid.toString(), size: bytes.length })

	// Build a one-block CAR containing the root (pattern from blob-upload.ts:58).
	const { writer, out } = CarWriter.create([cid])
	const chunks: Uint8Array[] = []
	const collect = (async () => {
		for await (const chunk of out) chunks.push(chunk)
	})()
	await writer.put({ cid, bytes })
	await writer.close()
	await collect
	const carBytes = concatChunks(chunks)

	const SyncWorker = getSyncWorker()
	const result = await SyncWorker.pushIOInWorker({
		blocks: [{ cid, bytes }],
		cid,
		prevCID: undefined,
		carBytes,
		ipnsPrivateKey: roster.pk,
	})
	if (!result.success) {
		const errors = result.events.filter(e => e.status === 'error')
		throw ERROR(`[pushRosterDoc] push I/O failed for ${roster.id}`, errors)
	}

	// pushIOInWorker treats IPNS-publish failure as non-fatal (returns success:true
	// once blocks are uploaded). Treat an IPNS failure as a real failure here —
	// otherwise we'd record lastPayloadHash for a publish whose name won't resolve,
	// and the next attempt would skip (stuck). Throwing leaves lastPayloadHash
	// untouched so retrying republishes.
	const ipnsError = result.events.find(e => e.op === 'ipns' && e.status === 'error')
	if (ipnsError) {
		throw ERROR(`[pushRosterDoc] block uploaded, but IPNS publish failed for ${roster.id} — the name will NOT resolve`, ipnsError.error)
	}
	const providerError = result.events.find(e => e.op === 'provider' && e.status === 'error')
	if (providerError) WARN('[pushRosterDoc] a provider push failed (block may not be retrievable)', providerError.error)

	const payloadHash = cid.toString()
	await agent.updateRoster(roster.id, {
		lastPush: dateNowIso(),
		lastCID: cid.toString(),
		lastPayloadHash: payloadHash,
	})
	LOG('[pushRosterDoc] published', { roster: roster.id, cid: cid.toString() })
	return cid
}

/**
 * Publish a self-roster if its payload has changed since the last push.
 * Dedups on the dag-json block CID string (which encodes the codec + content), so
 * a roster last published with the old RAW codec (or whose content changed)
 * republishes. Skips only when the CID exactly matches `lastPayloadHash`.
 */
export async function publishSelfRoster(roster: IRoster): Promise<CID | null> {
	const payload = buildSelfRosterPayload(roster)
	const { cid } = encodeBlock(payload) // dag-json codec (0x0129)
	const payloadHash = cid.toString()

	if (roster.lastPayloadHash === payloadHash) {
		VERBOSE('[publishSelfRoster] unchanged, skipping', { roster: roster.id })
		return null
	}
	DEBUG('[publishSelfRoster] payload changed, republishing', { roster: roster.id, payloadHash })
	return await pushRosterDoc(roster, payload)
}

/**
 * Re-publish every self-roster whose payload changed. Fire-and-forget friendly:
 * each roster is guarded independently so one failure doesn't abort the rest.
 * Intended to be called from the sync tick (and on `syncNow`) rather than wired
 * into `addShare`/`updateShare`/`deleteShare` directly.
 */
export async function maybeRepublishSelfRosters(): Promise<void> {
	const agent = useAgent()
	const rosters = agent.rosters
	if (!rosters.length) return
	DEBUG('[maybeRepublishSelfRosters]', { count: rosters.length })
	await Promise.allSettled(
		rosters.map(async (roster) => {
			try {
				await publishSelfRoster(roster)
			} catch (err) {
				WARN('[maybeRepublishSelfRosters] failed for', roster.id, err)
			}
		}),
	)
}

// =============================================================================
// Pull / resolve
// =============================================================================

/**
 * One-shot IPNS resolve: `GET <ipnsWatcherUrl>/name/<ipns>` → `{ value: '/ipfs/<cid>' }`.
 * This is the HTTP form of the same `/name/<ipns>` endpoint `IpnsWatcher` polls
 * (see `buildHttpUrl` in @wovin/core ipns-watcher); we replicate the URL here
 * rather than spin up a (long-lived) watcher for a single pull. Returns the
 * resolved root CID, or null if the name isn't published yet / endpoint unreachable.
 */
async function resolveRosterCID(ipnsId: string): Promise<CID | null> {
	const base = storageState.ipnsWatcherUrl?.replace(/\/+$/, '')
	if (!base) return null
	const url = `${base}/name/${ipnsId}`
	try {
		const res = await fetch(url)
		if (!res.ok) {
			if (res.status === 404) DEBUG('[resolveRosterCID] not yet published:', ipnsId)
			else WARN('[resolveRosterCID] name endpoint returned', res.status, ipnsId)
			return null
		}
		const record = (await res.json()) as { value?: string }
		if (!record.value?.startsWith('/ipfs/')) {
			WARN('[resolveRosterCID] unexpected value:', record.value, ipnsId)
			return null
		}
		const cid = CID.parse(record.value.replace('/ipfs/', ''))
		DEBUG('[resolveRosterCID] resolved', { ipnsId, cid: cid.toString() })
		return cid
	} catch (err) {
		WARN('[resolveRosterCID] failed', ipnsId, err)
		return null
	}
}

/** Gateways to try when fetching the block: configured providers first, public gateways as fallback. */
function fetchGateways(): readonly { url: string }[] {
	const configured = (storageState.gateways ?? []).map(g => ({ url: g.url })).filter(g => g.url)
	// Deduplicate by url so a gateway configured twice isn't hit twice.
	const seen = new Set<string>()
	const out: { url: string }[] = []
	for (const g of [...configured, ...DEFAULT_GATEWAYS]) {
		if (seen.has(g.url)) continue
		seen.add(g.url)
		out.push(g)
	}
	return out
}

/**
 * Pull + decode another depot's self-roster doc.
 *
 * 1. Resolve the IPNS name → root CID via the configured name-service endpoint.
 * 2. Fetch the single block as a one-block CAR (`retrieveCarBlock`, dag-scope=block),
 *    trying configured providers first and falling back to public gateways.
 * 3. `dagJson.decode` the root block bytes → `RosterPayload`.
 *
 * Returns null if the name isn't published yet (404). Throws on decode / fetch
 * failure so the caller can decide whether to retry.
 */
export async function retrieveRosterDoc(ipnsId: string): Promise<{ cid: CID; payload: RosterPayload } | null> {
	const cid = await resolveRosterCID(ipnsId)
	if (!cid) return null

	const reader = await retrieveCarBlock(cid, fetchGateways())
	let block = await reader.get(cid)
	if (!block) {
		// Fallback: walk the (single-block) CAR if the direct lookup missed.
		for await (const b of reader.blocks()) {
			if (b.cid.equals(cid)) {
				block = b
				break
			}
		}
	}
	if (!block) throw ERROR(`[retrieveRosterDoc] root block missing from CAR for ${cid.toString()}`)

	const payload = dagJson.decode(block.bytes) as RosterPayload
	DEBUG('[retrieveRosterDoc] decoded', {
		ipnsId,
		cid: cid.toString(),
		depotID: payload.depotID,
		shareCount: Object.keys(payload.shares ?? {}).length,
	})
	return { cid, payload }
}

// =============================================================================
// Sync sources
// =============================================================================

/**
 * For each share in `shares`, record `discovered[shareID] = depotID` (always —
 * incl. own/already-subscribed shares, so the UI can group existing subs under
 * this depot too), then `upsertSubscription(shareID, {name, autopull:true})`
 * unless the share is our own or we're already subscribed. Mutates `discovered`.
 */
async function subscribeToDiscoveredShares(
	shares: Record<string, string> | undefined,
	depotID: string,
	discovered: Record<string, string>,
): Promise<void> {
	const agent = useAgent()
	for (const [shareID, description] of Object.entries(shares ?? {})) {
		discovered[shareID] = depotID
		// Skip our own share — we publish it, no point subscribing to ourselves.
		if (agent.shares.some(s => s.id === shareID)) continue
		// Skip if already subscribed.
		if (agent.subscriptions.some(s => s.id === shareID)) continue
		try {
			await upsertSubscription(shareID as IpnsString, { name: description, autopull: true })
		} catch (err) {
			WARN('[subscribeToDiscoveredShares] upsertSubscription failed for', shareID, err)
		}
	}
}

/**
 * Pull + process a single roster source.
 *
 * - `kind:'self'`: pull the peer's roster, subscribe to its unknown shares
 *   (grouped under `payload.depotID`), and persist `{discovered, lastPull, lastCID}`.
 * - `kind:'registry'`: fetch the registry doc via task02's `fetchRegistrySource`
 *   (lazy-imported so this module compiles standalone), then pull each listed
 *   depot's self-roster and subscribe to its shares (grouped under the registry
 *   depot key). Persists `{discovered, lastPull}` (no single root CID for a registry).
 */
export async function syncRosterSource(source: IRosterSource): Promise<void> {
	const agent = useAgent()

	if (source.kind === 'registry') {
		DEBUG('[syncRosterSource] registry', { id: source.id, url: source.url })
		const { fetchRegistrySource } = await import('../data/roster-registry')
		const doc = await fetchRegistrySource(source.url)
		const discovered: Record<string, string> = { ...source.discovered }
		for (const [depotID, entry] of Object.entries(doc.depots ?? {})) {
			try {
				const res = await retrieveRosterDoc(entry.rosterIPNS)
				if (!res) {
					DEBUG('[syncRosterSource] depot not published yet, skipping', depotID)
					continue
				}
				await subscribeToDiscoveredShares(res.payload.shares, depotID, discovered)
			} catch (err) {
				WARN('[syncRosterSource] depot pull failed for', depotID, err)
			}
		}
		await agent.updateRosterSource(source.id, {
			discovered,
			lastPull: dateNowIso(),
		})
		LOG('[syncRosterSource] registry done', { id: source.id, discoveredCount: Object.keys(discovered).length })
		return
	}

	// kind === 'self'
	DEBUG('[syncRosterSource] self', { id: source.id, url: source.url })
	const result = await retrieveRosterDoc(source.url)
	if (!result) {
		DEBUG('[syncRosterSource] not published yet, skipping', source.url)
		return
	}
	const { cid, payload } = result

	const discovered: Record<string, string> = { ...source.discovered }
	await subscribeToDiscoveredShares(payload.shares, payload.depotID, discovered)

	await agent.updateRosterSource(source.id, {
		name: payload.rosterName ?? source.name ?? payload.depotName,
		discovered,
		lastPull: dateNowIso(),
		lastCID: cid.toString(),
	})
	LOG('[syncRosterSource] done', { id: source.id, discoveredCount: Object.keys(discovered).length })
}

/**
 * Pull + process every roster source now. Each source is guarded independently
 * (allSettled) so one broken peer doesn't abort the rest.
 */
export async function syncRosterSourcesNow(): Promise<void> {
	const agent = useAgent()
	const sources = agent.rosterSources
	if (!sources.length) return
	DEBUG('[syncRosterSourcesNow]', { count: sources.length })
	await Promise.allSettled(
		sources.map(async (source) => {
			try {
				await syncRosterSource(source)
			} catch (err) {
				WARN('[syncRosterSourcesNow] failed for', source.id, err)
			}
		}),
	)
}

/**
 * Entry point for roster sync. Performs an initial pull of all sources plus a
 * republish of stale self-rosters. The periodic re-run is driven by the sync
 * tick (`performSync` in sync-service.ts), which calls `syncRosterSourcesNow`
 * and `maybeRepublishSelfRosters` directly.
 */
export async function startRosterSync(): Promise<void> {
	LOG('[startRosterSync]')
	await Promise.allSettled([syncRosterSourcesNow(), maybeRepublishSelfRosters()])
}
